import React, { useEffect, useState } from 'react';
import '../styles/app.scss';
import Modal from './Modal';
import Button from './Button';
import { StateProvider } from '../stateManagement/store';
import { stateEventObserver, callbackEventProvider } from '../services/EventObserver';
import EventTypes from '../constants/eventTypes';
import { setNetworkType } from '../stateManagement/stateConstants';
import { IConnectParams, IDisconnectParams } from '../interfaces';
import { getMobileOperatingSystem } from '../services/utils';
import { getInPageProviderName } from '../services/inPageProviderService';
import { INPAGE_KEY, WALLET_TYPES } from '../constants/wallet';

export interface IApp {
  autoConnectInPageProvider?: boolean
  showBalance?: boolean
  rpcUrl: string
  networkId: number
  ledgerPath?: string
  pendingTx?: number
  onConnect: (params: IConnectParams) => void
  onConnectRequest: (params: any) => void
  onConnectError: (params: any) => void
  onDisconnect: (params: IDisconnectParams) => void
  onClick?: () => void
  afterConnect?: () => void
}

export const App: React.FC<IApp> = ({
  rpcUrl,
  networkId,
  ledgerPath = '',
  pendingTx = 0,
  onConnect = null,
  onConnectRequest = null,
  onConnectError = null,
  onDisconnect = null,
  onClick,
  afterConnect,
  autoConnectInPageProvider,
  showBalance,
}) => {
  const [once, setOnce] = useState(false);

  useEffect(() => {
    callbackEventProvider.emit(EventTypes.MOUNTED, true);

    return () => {
      callbackEventProvider.emit(EventTypes.MOUNTED, false);
      callbackEventProvider.off({ eventType: EventTypes.CONNECT_SUCCESS_COMPONENT, callback: onConnect });
      callbackEventProvider.off({ eventType: EventTypes.CONNECT_REQUEST_COMPONENT, callback: onConnectRequest });
      callbackEventProvider.off({ eventType: EventTypes.CONNECT_ERROR_COMPONENT, callback: onConnectError });
      callbackEventProvider.off({ eventType: EventTypes.DISCONNECT, callback: onDisconnect });
      callbackEventProvider.off({ eventType: EventTypes.AFTER_CONNECT });
    };
  }, []);
  useEffect(() => {
    if (onConnect) {
      callbackEventProvider.on({ eventType: EventTypes.CONNECT_SUCCESS_COMPONENT, callback: onConnect });
    }
  }, [onConnect]);
  useEffect(() => {
    if (onConnectRequest) {
      callbackEventProvider.on({ eventType: EventTypes.CONNECT_REQUEST_COMPONENT, callback: onConnectRequest });
    }
  }, [onConnectRequest]);
  useEffect(() => {
    if (onConnectError) {
      callbackEventProvider.on({ eventType: EventTypes.CONNECT_ERROR_COMPONENT, callback: onConnectError });
    }
  }, [onConnectError]);
  useEffect(() => {
    if (onDisconnect) {
      callbackEventProvider.on({ eventType: EventTypes.DISCONNECT, callback: onDisconnect });
    }
  }, [onDisconnect]);
  useEffect(() => {
    if (afterConnect) {
      callbackEventProvider.on({ eventType: EventTypes.AFTER_CONNECT, callback: afterConnect });
    }
  }, [afterConnect]);

  useEffect(() => {
    stateEventObserver.emit(EventTypes.DISPATCH, setNetworkType.emit({ rpcUrl, networkId, ledgerPath }));
  },
  [rpcUrl, ledgerPath, networkId],
  );

  useEffect(() => {
    if (!once && rpcUrl && networkId && autoConnectInPageProvider) {
      const isMobileOS = getMobileOperatingSystem() !== 'unknown';
      const inPage = getInPageProviderName();
      const enforceConnection = localStorage.getItem(INPAGE_KEY);
      if (isMobileOS && inPage && !enforceConnection) {
        stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { rpcUrl, networkId, connectionType: WALLET_TYPES.inPageProvider });
        setOnce(true);
      }
    }
  }, [rpcUrl, networkId, once, autoConnectInPageProvider]);

  return (
    <StateProvider eventObserver={stateEventObserver}>
      <Button pendingTx={pendingTx} onClick={onClick} showBalance={showBalance} />
      <Modal />
    </StateProvider>
  );
};

export default App;
